import { useState, useEffect } from 'react';

/**
 * Hook avançado para gerenciar diferentes tipos de onboarding
 * Suporta onboarding condicional baseado em contexto
 */
export const useAdvancedOnboarding = () => {
  const [onboardingStates, setOnboardingStates] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Tipos de onboarding disponíveis
  const ONBOARDING_TYPES = {
    PARENTS_FIRST_TIME: 'parentsFirstTime',
    PARENTS_NEW_STUDENT: 'parentsNewStudent',
    PARENTS_NEW_FEATURES: 'parentsNewFeatures',
    MOBILE_APP_PROMOTION: 'mobileAppPromotion'
  };

  useEffect(() => {
    const loadOnboardingStates = () => {
      try {
        const states = {};
        
        Object.values(ONBOARDING_TYPES).forEach(type => {
          const completed = localStorage.getItem(`${type}Completed`);
          const skipped = localStorage.getItem(`${type}Skipped`);
          const date = localStorage.getItem(`${type}Date`);
          
          states[type] = {
            completed: !!completed,
            skipped: !!skipped,
            date: date ? new Date(date) : null,
            shouldShow: !completed && !skipped
          };
        });
        
        setOnboardingStates(states);
      } catch (error) {
        console.error('Erro ao carregar estados do onboarding:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOnboardingStates();
  }, []);

  // Verificar se deve mostrar onboarding baseado em condições
  const shouldShowOnboarding = (type, conditions = {}) => {
    if (isLoading) return false;
    
    const state = onboardingStates[type];
    if (!state || !state.shouldShow) return false;

    // Condições específicas por tipo
    switch (type) {
      case ONBOARDING_TYPES.PARENTS_FIRST_TIME:
        // Mostrar apenas se é a primeira vez no portal
        return !localStorage.getItem('parentsPortalVisited');
        
      case ONBOARDING_TYPES.PARENTS_NEW_STUDENT:
        // Mostrar se o usuário tem um novo estudante
        return conditions.hasNewStudent === true;
        
      case ONBOARDING_TYPES.PARENTS_NEW_FEATURES:
        // Mostrar se há features novas desde a última visita
        const lastFeatureUpdate = localStorage.getItem('lastFeatureUpdate');
        const currentVersion = '2024.1.0'; // Versão atual das features
        return lastFeatureUpdate !== currentVersion;
        
      case ONBOARDING_TYPES.MOBILE_APP_PROMOTION:
        // Mostrar após X dias de uso se não baixou o app
        const firstVisit = localStorage.getItem('parentsPortalFirstVisit');
        if (!firstVisit) return false;
        
        const daysSinceFirstVisit = (Date.now() - new Date(firstVisit).getTime()) / (1000 * 60 * 60 * 24);
        const hasDownloadedApp = localStorage.getItem('mobileAppDownloaded');
        
        return daysSinceFirstVisit >= 3 && !hasDownloadedApp;
        
      default:
        return state.shouldShow;
    }
  };

  // Marcar onboarding como completo
  const markAsCompleted = (type) => {
    localStorage.setItem(`${type}Completed`, 'true');
    localStorage.setItem(`${type}Date`, new Date().toISOString());
    
    setOnboardingStates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        completed: true,
        shouldShow: false,
        date: new Date()
      }
    }));
  };

  // Marcar onboarding como pulado
  const markAsSkipped = (type) => {
    localStorage.setItem(`${type}Skipped`, 'true');
    localStorage.setItem(`${type}Date`, new Date().toISOString());
    
    setOnboardingStates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        skipped: true,
        shouldShow: false,
        date: new Date()
      }
    }));
  };

  // Resetar onboarding específico
  const resetOnboarding = (type) => {
    localStorage.removeItem(`${type}Completed`);
    localStorage.removeItem(`${type}Skipped`);
    localStorage.removeItem(`${type}Date`);
    
    setOnboardingStates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        completed: false,
        skipped: false,
        shouldShow: true,
        date: null
      }
    }));
  };

  // Marcar primeira visita ao portal
  const markPortalVisited = () => {
    if (!localStorage.getItem('parentsPortalFirstVisit')) {
      localStorage.setItem('parentsPortalFirstVisit', new Date().toISOString());
    }
    localStorage.setItem('parentsPortalVisited', 'true');
    localStorage.setItem('parentsPortalLastVisit', new Date().toISOString());
  };

  // Marcar que o app foi baixado
  const markAppDownloaded = () => {
    localStorage.setItem('mobileAppDownloaded', 'true');
    localStorage.setItem('mobileAppDownloadDate', new Date().toISOString());
  };

  // Atualizar versão das features
  const updateFeatureVersion = (version) => {
    localStorage.setItem('lastFeatureUpdate', version);
  };

  return {
    ONBOARDING_TYPES,
    onboardingStates,
    isLoading,
    shouldShowOnboarding,
    markAsCompleted,
    markAsSkipped,
    resetOnboarding,
    markPortalVisited,
    markAppDownloaded,
    updateFeatureVersion
  };
};

export default useAdvancedOnboarding;
